import type { WPriceRangeView, WProductLookView, WSkuPreview } from '@zola/svc-web-api-ts-client';

export const getProductPricesSchema = (
  activeProductLook: WProductLookView,
  activeSku?: WSkuPreview | null
) => {
  const getPriceRange = (range?: WPriceRangeView) => {
    if (range?.min_price === range?.max_price) {
      return {
        lowPrice: range?.min_price,
        highPrice: undefined,
      };
    }
    return {
      lowPrice: range?.min_price,
      highPrice: range?.max_price,
    };
  };

  if (activeSku && activeSku.reference_price?.type) {
    // determine reference price from SKU
    if (['MSRP', 'COMPAREATPRICE', 'DISCOUNTPRICE'].includes(activeSku.reference_price.type)) {
      return {
        lowPrice: activeSku.reference_price.amount,
        highPrice: undefined,
      };
    }

    return { lowPrice: activeSku.price_cents, highPrice: undefined };
  }

  if (activeProductLook.reference_price_range) {
    // determine reference price range from product look
    return getPriceRange(activeProductLook.reference_price_range);
  }

  return getPriceRange(activeProductLook.price_range);
};

const getPriceTypeMeta = (price: {
  lowPrice: number | undefined;
  highPrice: number | undefined;
}) => {
  if (!price.highPrice && price.lowPrice) {
    return <meta itemProp="price" content={`${price.lowPrice / 100}`} />;
  }

  if (price.highPrice && price.lowPrice) {
    return (
      <>
        <meta itemProp="lowPrice" content={`${price.lowPrice / 100}`} />
        <meta itemProp="highPrice" content={`${price.lowPrice / 100}`} />
      </>
    );
  }
  return <></>;
};
/**
 * Helper function that renders schema information for product details.
 * The sole purpose of this function is to support Google Rich Snippet for better SEO.
 */
type RenderProductSchemaProps = {
  activeProductLook?: WProductLookView;
  activeSku?: WSkuPreview | null;
  productImage: string;
  productBrand: string;
  productKey: string;
};
export const renderProductSchema = (props: RenderProductSchemaProps) => {
  const { activeProductLook, activeSku, productImage, productBrand, productKey } = props;
  if (!activeProductLook) {
    return null;
  }
  const price = getProductPricesSchema(activeProductLook, activeSku);
  const offerType = price.highPrice
    ? 'https://schema.org/AggregateOffer'
    : 'https://schema.org/Offer';

  const url = new URL(
    `/shop/product/${productKey}`,
    `https://www.zola.com/shop/product`
  ).toString();

  const offerSchema = (
    <div itemProp="offers" itemType={offerType} itemScope>
      <link itemProp="url" href={`${url}`} />
      <meta itemProp="availability" content="https://schema.org/InStock" />
      <meta itemProp="priceCurrency" content="USD" />
      {getPriceTypeMeta(price)}
      <div itemProp="seller" itemType="https://schema.org/Organization" itemScope>
        <meta itemProp="name" content="Zola" />
      </div>
    </div>
  );

  return (
    <>
      <link itemProp="image" href={productImage} />
      <div itemProp="brand" itemType="https://schema.org/Thing" itemScope>
        <meta itemProp="name" content={productBrand} />
      </div>
      {activeSku && <meta itemProp="sku" content={activeSku && activeSku.id} />}
      {offerSchema}
    </>
  );
};
